import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Card,
    Typography,
    message,
    Popconfirm,
    Space,
    InputNumber,
    Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import moment from 'moment';

const { Title } = Typography;
const { Option } = Select;

const HairDyeManagement = () => {
    const [dyes, setDyes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingDye, setEditingDye] = useState(null);
    const [form] = Form.useForm();
    const [searchForm] = Form.useForm();
    const navigate = useNavigate();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const fetchDyes = async (page = 1, pageSize = 10, searchValues = {}) => {
        setLoading(true);
        try {
            const params = { pageNum: page, pageSize, ...searchValues };
            const response = await apiService.hairDye.getList(params);
            if (response.code === 200 && response.data && Array.isArray(response.data.records)) {
                const validRecords = response.data.records.filter(record => record && record.id);
                setDyes(validRecords);
                setPagination({
                    current: response.data.current,
                    pageSize: response.data.size,
                    total: response.data.total,
                });
            } else {
                message.error(response.message || '获取染膏列表失败');
            }
        } catch (error) {
            // Error is already handled by the interceptor
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDyes();
    }, []);

    const handleTableChange = (pagination) => {
        const searchValues = searchForm.getFieldsValue();
        fetchDyes(pagination.current, pagination.pageSize, searchValues);
    };

    const handleSearch = (values) => {
        fetchDyes(1, pagination.pageSize, values);
    };

    const handleReset = () => {
        searchForm.resetFields();
        fetchDyes(1, pagination.pageSize);
    };

    const handleAdd = () => {
        setEditingDye(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingDye(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await apiService.hairDye.delete(id);
            if (response.code === 200) {
                message.success('删除成功');
                fetchDyes(pagination.current, pagination.pageSize, searchForm.getFieldsValue());
            } else {
                message.error(response.message || '删除失败');
            }
        } catch (error) {
            // Error is already handled by the interceptor
        }
    };

    const handleSubmit = async (values) => {
        try {
            let response;
            if (editingDye) {
                response = await apiService.hairDye.update(editingDye.id, values);
            } else {
                response = await apiService.hairDye.create(values);
            }

            if (response.code === 200) {
                message.success(editingDye ? '更新成功' : '创建成功');
                setModalVisible(false);
                fetchDyes(pagination.current, pagination.pageSize, searchForm.getFieldsValue());
            } else {
                message.error(response.message || '操作失败');
            }
        } catch (error) {
            // Error is already handled by the interceptor
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', sorter: (a, b) => a.id - b.id },
        { title: '染膏名称', dataIndex: 'dyeName', key: 'dyeName' },
        { title: '品牌', dataIndex: 'brand', key: 'brand' },
        { title: '色号', dataIndex: 'colorNumber', key: 'colorNumber' },
        { title: '颜色名称', dataIndex: 'colorName', key: 'colorName' },
        { title: '协议代码', dataIndex: 'protocolCode', key: 'protocolCode' },
        { title: '协议数值', dataIndex: 'protocolValue', key: 'protocolValue' },
        { title: 'DTO字段名', dataIndex: 'colorField', key: 'colorField' },
        { title: '仓位前缀', dataIndex: 'locationPrefix', key: 'locationPrefix' },
        {
            title: '状态',
            dataIndex: 'isValid',
            key: 'isValid',
            render: (isValid) => (
                <Tag color={isValid ? 'green' : 'red'}>{isValid ? '有效' : '无效'}</Tag>
            ),
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 210,
            render: (_, record) => (
                <Space size="middle">
                    <Button type="link" onClick={() => navigate(`/hair-dye/stock/${record.id}`)}>
                        库存
                    </Button>
                    <Button type="link" onClick={() => navigate(`/hair-dye/specs/${record.id}`)}>
                        规格管理
                    </Button>
                    <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm title="确定删除吗？" onConfirm={() => handleDelete(record.id)}>
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card>
            <Title level={4}>染膏管理</Title>
            <Form form={searchForm} layout="inline" onFinish={handleSearch} style={{ marginBottom: 20 }}>
                <Form.Item name="dyeName">
                    <Input placeholder="染膏名称" />
                </Form.Item>
                <Form.Item name="brand">
                    <Input placeholder="品牌" />
                </Form.Item>
                <Form.Item name="colorNumber">
                    <Input placeholder="色号" />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
                </Form.Item>
                <Form.Item>
                    <Button onClick={handleReset}>重置</Button>
                </Form.Item>
            </Form>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginBottom: 20 }}>
                添加染膏
            </Button>
            <Table
                columns={columns}
                dataSource={dyes}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
            />
            <Modal
                title={editingDye ? '编辑染膏' : '添加染膏'}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ isValid: 1 }}>
                    <Form.Item name="dyeName" label="染膏名称" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="brand" label="品牌" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="colorNumber" label="色号" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="colorName" label="颜色名称">
                        <Input />
                    </Form.Item>
                    <Form.Item name="protocolCode" label="协议颜色代码" tooltip="例如: 0x30">
                        <Input />
                    </Form.Item>
                    <Form.Item name="protocolValue" label="协议数值" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="colorField" label="DTO字段名" rules={[{ required: true }]} tooltip="例如: gray">
                        <Input />
                    </Form.Item>
                    <Form.Item name="locationPrefix" label="仓位前缀" tooltip="例如: A, B, C">
                        <Input />
                    </Form.Item>
                    <Form.Item name="isValid" label="状态" rules={[{ required: true }]}>
                        <Select>
                            <Option value={1}>有效</Option>
                            <Option value={0}>无效</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default HairDyeManagement;
